import React, { useEffect, useState, createElement } from 'react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { FileText, Printer, Mail } from 'lucide-react';
const sections = [{
  id: 'definitions',
  label: 'Definitions'
}, {
  id: 'contract',
  label: 'The Contract'
}, {
  id: 'professional-duties',
  label: 'Professional Duties'
}, {
  id: 'fees',
  label: 'Fees & Payment',
  highlight: true
}, {
  id: 'court-attendance',
  label: 'Court Attendance',
  highlight: true
}, {
  id: 'timelines',
  label: 'Timelines'
}, {
  id: 'data-protection',
  label: 'Data Protection & Safety'
}, {
  id: 'intellectual-property',
  label: 'Intellectual Property'
}, {
  id: 'liability',
  label: 'Liability',
  highlight: true
}, {
  id: 'expert-allocation',
  label: 'Expert Allocation'
}, {
  id: 'complaints',
  label: 'Complaints'
}, {
  id: 'termination',
  label: 'Termination'
}, {
  id: 'governing-law',
  label: 'Governing Law'
}];
const policyLinks = [{
  href: '/privacy-policy',
  label: 'Privacy & Cookie Policy'
}, {
  href: '/cancellation-policy',
  label: 'Cancellation Policy'
}, {
  href: '/terms-conditions',
  label: 'Terms & Conditions',
  active: true
}];
export function TermsConditions() {
  const [activeSection, setActiveSection] = useState('definitions');
  useEffect(() => {
    document.title = 'Terms & Conditions | UK Medicolegal Assessment';
    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    setMeta('description', 'Professional terms for medico-legal instructions, expert witness fees, and payment structures for solicitors and insurers.');
    setMeta('robots', 'index, follow');
  }, []);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    }, {
      rootMargin: '-20% 0px -60% 0px'
    });
    sections.forEach(({
      id
    }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);
  return <div className="min-h-screen bg-white" data-id="element-1111">
      <Navigation data-id="element-1112" />

      <div className="pt-32 pb-20 md:pt-40 md:pb-24" data-id="element-1113">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8" data-id="element-1114">
          <div className="lg:grid lg:grid-cols-[240px_1fr] lg:gap-16" data-id="element-1115">
            {/* Sticky Sidebar */}
            <aside className="hidden lg:block" data-id="element-1116">
              <div className="sticky top-32" data-id="element-1117">
                <nav aria-label="Legal policies navigation" className="mb-8" data-id="element-1118">
                  <p className="text-xs font-bold text-ink-400 uppercase tracking-wider mb-3" data-id="element-1119">
                    Policies
                  </p>
                  <ul className="space-y-1" data-id="element-1120">
                    {policyLinks.map(link => <li key={link.href} data-id="element-1121">
                        <a href={link.href} className={`block text-sm py-2 px-3 rounded-lg transition-colors ${link.active ? 'bg-teal-50 text-teal-700 font-semibold' : 'text-ink-500 hover:text-ink-700 hover:bg-warm-50'}`} data-id="element-1122">
                          {link.label}
                        </a>
                      </li>)}
                  </ul>
                </nav>

                <nav aria-label="Page sections navigation" data-id="element-1123">
                  <p className="text-xs font-bold text-ink-400 uppercase tracking-wider mb-3" data-id="element-1124">
                    On this page
                  </p>
                  <ul className="space-y-1" data-id="element-1125">
                    {sections.map(section => <li key={section.id} data-id="element-1126">
                        <a href={`#${section.id}`} className={`block text-sm py-2 px-3 rounded-lg transition-colors ${activeSection === section.id ? 'bg-warm-100 text-ink-900 font-semibold' : section.highlight ? 'text-ink-700 font-semibold hover:bg-warm-50' : 'text-ink-500 hover:text-ink-700 hover:bg-warm-50'}`} data-id="element-1127">
                          {section.label}
                        </a>
                      </li>)}
                  </ul>
                </nav>
              </div>
            </aside>

            {/* Main Content */}
            <main className="max-w-[800px]" data-id="element-1128">
              {/* Mobile policy nav */}
              <div className="lg:hidden flex gap-2 mb-8 overflow-x-auto pb-2" data-id="element-1129">
                {policyLinks.map(link => <a key={link.href} href={link.href} className={`whitespace-nowrap text-sm py-2 px-4 rounded-full border transition-colors ${link.active ? 'bg-teal-50 border-teal-200 text-teal-700 font-semibold' : 'border-warm-200 text-ink-500 hover:border-teal-300'}`} data-id="element-1130">
                    {link.label}
                  </a>)}
              </div>

              {/* Header */}
              <div className="flex items-start justify-between mb-10" data-id="element-1131">
                <div data-id="element-1132">
                  <div className="flex items-center gap-3 mb-4" data-id="element-1133">
                    <div className="w-10 h-10 bg-warm-50 rounded-xl border border-warm-200 flex items-center justify-center" role="img" aria-label="Professional legal document icon representing agency terms and conditions" data-id="element-1134">
                      <FileText className="h-5 w-5 text-slate-600" aria-hidden="true" data-id="element-1135" />
                    </div>
                    <h1 className="text-3xl md:text-4xl font-bold text-[#1A2B3C] tracking-tight" data-id="element-1136">
                      Terms & Conditions
                    </h1>
                  </div>
                  <p className="text-sm text-ink-400 font-medium" data-id="element-1137">
                    Effective Date: March 2026 · UK Medicolegal Assessment Ltd
                  </p>
                </div>

                {/* Print button */}
                <button onClick={() => window.print()} className="hidden md:inline-flex items-center gap-2 text-sm font-semibold text-ink-500 hover:text-ink-700 border border-[#E2E8F0] rounded-xl px-4 py-2.5 hover:bg-warm-50 transition-colors flex-shrink-0" aria-label="Print this page" data-id="element-1138">
                  <Printer className="h-4 w-4" data-id="element-1139" />
                  Print
                </button>
              </div>

              {/* 1. Definitions */}
              <section id="definitions" className="mb-14" data-id="element-1140">
                <h2 className="text-2xl font-bold text-[#1A2B3C] mb-6" data-id="element-1141">
                  1. Definitions
                </h2>
                <div className="bg-warm-50 border border-[#E2E8F0] rounded-2xl overflow-hidden" data-id="element-1142">
                  <table className="w-full" data-id="element-1143">
                    <tbody className="divide-y divide-[#E2E8F0]" data-id="element-1144">
                      <tr data-id="element-1145">
                        <td className="px-6 py-4 text-ink-900 font-bold whitespace-nowrap align-top" data-id="element-1146">
                          "The Agency"
                        </td>
                        <td className="px-6 py-4 text-ink-600" data-id="element-1147">
                          UK Medico-Legal Assessments.
                        </td>
                      </tr>
                      <tr data-id="element-1148">
                        <td className="px-6 py-4 text-ink-900 font-bold whitespace-nowrap align-top" data-id="element-1149">
                          "Instructing Party"
                        </td>
                        <td className="px-6 py-4 text-ink-600" data-id="element-1150">
                          The solicitor, law firm, or insurer providing the
                          instruction.
                        </td>
                      </tr>
                      <tr data-id="element-1151">
                        <td className="px-6 py-4 text-ink-900 font-bold whitespace-nowrap align-top" data-id="element-1152">
                          "The Expert"
                        </td>
                        <td className="px-6 py-4 text-ink-600" data-id="element-1153">
                          The GMC-registered specialist allocated to provide the
                          services.
                        </td>
                      </tr>
                      <tr data-id="element-1154">
                        <td className="px-6 py-4 text-ink-900 font-bold whitespace-nowrap align-top" data-id="element-1155">
                          "The Subject"
                        </td>
                        <td className="px-6 py-4 text-ink-600" data-id="element-1156">
                          The individual being assessed.
                        </td>
                      </tr>
                      <tr data-id="element-1157">
                        <td className="px-6 py-4 text-ink-900 font-bold whitespace-nowrap align-top" data-id="element-1158">
                          "Report"
                        </td>
                        <td className="px-6 py-4 text-ink-600" data-id="element-1159">
                          Any medico-legal report, assessment, or written
                          opinion produced by the Expert.
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              {/* 2. The Contract */}
              <section id="contract" className="mb-14" data-id="element-1160">
                <h2 className="text-2xl font-bold text-[#1A2B3C] mb-6" data-id="element-1161">
                  2. The Contract
                </h2>
                <p className="text-ink-600 leading-relaxed mb-6" style={{
                lineHeight: '1.8'
              }} data-id="element-1162">
                  By providing a written instruction, you are deemed to have
                  accepted these terms. A contract exists only once we confirm
                  acceptance of your instruction in writing. We reserve the
                  right to decline any instruction due to conflict of interest
                  or clinical appropriateness.
                </p>
                <p className="text-ink-600 leading-relaxed" style={{
                lineHeight: '1.8'
              }} data-id="element-1163">
                  Instructions must include all relevant case information,
                  medical records, and specific questions to be addressed. We
                  reserve the right to request additional documentation if
                  necessary to ensure a thorough and accurate assessment.
                </p>
              </section>

              {/* 3. Professional Duties */}
              <section id="professional-duties" className="mb-14" data-id="element-1164">
                <h2 className="text-2xl font-bold text-[#1A2B3C] mb-6" data-id="element-1165">
                  3. Professional Duties
                </h2>
                <p className="text-ink-600 leading-relaxed mb-6" style={{
                lineHeight: '1.8'
              }} data-id="element-1166">
                  All Experts on our panel operate under an overriding duty to
                  the Court (CPR Part 35, FPR Part 25, or CrPR Part 19). Our
                  reports are independent, objective, and impartial.
                </p>
                <p className="text-ink-600 leading-relaxed" style={{
                lineHeight: '1.8'
              }} data-id="element-1167">
                  The Expert's duty to the Court overrides any obligation to the
                  Instructing Party. Reports will contain the Expert's honest
                  professional opinion, which may not always support the
                  Instructing Party's case.
                </p>
              </section>

              {/* 4. Fees & Payment */}
              <section id="fees" className="mb-14" data-id="element-1168">
                <h2 className="text-2xl font-bold text-[#1A2B3C] mb-6" data-id="element-1169">
                  4. Fees & Payment
                </h2>

                <div className="bg-[#1A2B3C] text-white rounded-2xl p-6 md:p-8 mb-8" data-id="element-1170">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-1171">
                    <div className="bg-slate-700/60 rounded-xl p-4 border border-slate-600" data-id="element-1172">
                      <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1" data-id="element-1173">
                        Payment Terms
                      </p>
                      <p className="text-lg font-bold text-white" data-id="element-1174">30 days</p>
                      <p className="text-sm text-slate-300" data-id="element-1175">
                        From date of invoice
                      </p>
                    </div>
                    <div className="bg-slate-700/60 rounded-xl p-4 border border-slate-600" data-id="element-1176">
                      <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1" data-id="element-1177">
                        Late Payment Interest
                      </p>
                      <p className="text-lg font-bold text-white" data-id="element-1178">8% p.a.</p>
                      <p className="text-sm text-slate-300" data-id="element-1179">
                        Above BoE base rate
                      </p>
                    </div>
                    <div className="bg-slate-700/60 rounded-xl p-4 border border-slate-600" data-id="element-1180">
                      <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1" data-id="element-1181">
                        Legal Aid
                      </p>
                      <p className="text-lg font-bold text-white" data-id="element-1182">LAA Rates</p>
                      <p className="text-sm text-slate-300" data-id="element-1183">
                        Agreed in writing prior
                      </p>
                    </div>
                  </div>
                </div>

                <h3 className="text-lg font-bold text-[#1A2B3C] mb-3" data-id="element-1184">
                  4.1 Fee Structure
                </h3>
                <p className="text-ink-600 leading-relaxed mb-5" style={{
                lineHeight: '1.8'
              }} data-id="element-1185">
                  Fees are quoted in advance and confirmed in writing. Fees
                  typically include examination, report preparation, and
                  standard correspondence. Additional charges may apply for:
                </p>
                <ul className="space-y-2 mb-6" data-id="element-1186">
                  {['Urgent instructions requiring expedited service', 'Court attendance and conferences', 'Supplementary reports and addendums', 'Extensive medical record review', 'Cancelled appointments (see Cancellation Policy)'].map((item, i) => <li key={i} className="flex items-start gap-3 text-ink-600" data-id="element-1187">
                      <span className="text-teal-500 mt-1 font-bold" data-id="element-1188">•</span>
                      <span style={{
                    lineHeight: '1.8'
                  }} data-id="element-1189">
                        {item}
                      </span>
                    </li>)}
                </ul>

                <h3 className="text-lg font-bold text-[#1A2B3C] mb-3" data-id="element-1190">
                  4.2 Payment Terms
                </h3>
                <p className="text-ink-600 leading-relaxed mb-6" style={{
                lineHeight: '1.8'
              }} data-id="element-1191">
                  Payment is strictly 30 days from the date of invoice. We
                  reserve the right to charge interest at 8% per annum above the
                  Bank of England base rate on overdue accounts, in accordance
                  with the Late Payment of Commercial Debts (Interest) Act 1998.
                </p>

                <h3 className="text-lg font-bold text-[#1A2B3C] mb-3" data-id="element-1192">
                  4.3 Legal Aid Cases
                </h3>
                <p className="text-ink-600 leading-relaxed mb-6" style={{
                lineHeight: '1.8'
              }} data-id="element-1193">
                  We work within LAA approved limits, provided these are agreed
                  upon in writing prior to instruction. The Instructing Party
                  remains liable for all fees in the event that Legal Aid
                  funding is withdrawn or the case is no longer publicly funded.
                </p>

                <h3 className="text-lg font-bold text-[#1A2B3C] mb-3" data-id="element-1194">
                  4.4 Instructing Party Liability
                </h3>
                <p className="text-ink-600 leading-relaxed mb-6" style={{
                lineHeight: '1.8'
              }} data-id="element-1195">
                  The Instructing Party is solely liable for all fees and
                  charges arising from the instruction. Fees are payable
                  regardless of case outcome, settlement status, funding
                  arrangements, or whether the Report is ultimately used in
                  proceedings.
                </p>

                <h3 className="text-lg font-bold text-[#1A2B3C] mb-3" data-id="element-1196">
                  4.5 Disbursements
                </h3>
                <p className="text-ink-600 leading-relaxed" style={{
                lineHeight: '1.8'
              }} data-id="element-1197">
                  Any disbursements (travel expenses, venue hire, etc.) will be
                  invoiced separately and must be paid within agreed terms.
                </p>
              </section>

              {/* 5. Court & Conference Attendance */}
              <section id="court-attendance" className="mb-14" data-id="element-1198">
                <h2 className="text-2xl font-bold text-[#1A2B3C] mb-6" data-id="element-1199">
                  5. Court & Conference Attendance
                </h2>

                <div className="bg-warm-50 border border-[#E2E8F0] rounded-2xl p-6 mb-6" data-id="element-1200">
                  <p className="text-ink-900 font-bold mb-2" data-id="element-1201">
                    Minimum Notice: 6 weeks
                  </p>
                  <p className="text-ink-600 text-sm leading-relaxed" data-id="element-1202">
                    We require a minimum of 6 weeks' notice for Court attendance
                    to ensure Expert availability. Separate quotes will be
                    provided and must be accepted in writing before the Expert
                    makes travel arrangements.
                  </p>
                </div>

                <p className="text-ink-600 leading-relaxed mb-6" style={{
                lineHeight: '1.8'
              }} data-id="element-1203">
                  Court attendance fees are separate from report fees and will
                  be quoted individually. Fees cover preparation time, travel,
                  waiting time, and time giving evidence. Cancellation of court
                  attendance is subject to the terms set out in our{' '}
                  <a href="/cancellation-policy" className="text-teal-600 hover:text-teal-700 font-semibold transition-colors" data-id="element-1204">
                    Cancellation Policy
                  </a>
                  .
                </p>
                <p className="text-ink-600 leading-relaxed" style={{
                lineHeight: '1.8'
              }} data-id="element-1205">
                  Where the Expert is required to attend a case conference,
                  joint discussion, or telephone hearing, separate fees will
                  apply and will be quoted in advance.
                </p>
              </section>

              {/* 6. Timelines */}
              <section id="timelines" className="mb-14" data-id="element-1206">
                <h2 className="text-2xl font-bold text-[#1A2B3C] mb-6" data-id="element-1207">
                  6. Timelines
                </h2>

                <h3 className="text-lg font-bold text-[#1A2B3C] mb-3" data-id="element-1208">
                  6.1 Standard Delivery
                </h3>
                <p className="text-ink-600 leading-relaxed mb-6" style={{
                lineHeight: '1.8'
              }} data-id="element-1209">
                  1–3 weeks from the date of assessment, subject to receipt of
                  all relevant medical records. Urgent instructions may be
                  accommodated where Expert availability permits — mark "Urgent"
                  in the subject line of your instruction.
                </p>

                <h3 className="text-lg font-bold text-[#1A2B3C] mb-3" data-id="element-1210">
                  6.2 Delays
                </h3>
                <p className="text-ink-600 leading-relaxed mb-6" style={{
                lineHeight: '1.8'
              }} data-id="element-1211">
                  We are not liable for delays caused by the late provision of
                  documentation, the Subject's non-attendance, or circumstances
                  beyond our reasonable control. Where delays are anticipated,
                  we will notify the Instructing Party promptly and provide a
                  revised timescale.
                </p>

                <h3 className="text-lg font-bold text-[#1A2B3C] mb-3" data-id="element-1212">
                  6.3 Amendments
                </h3>
                <p className="text-ink-600 leading-relaxed" style={{
                lineHeight: '1.8'
              }} data-id="element-1213">
                  Minor typographical corrections will be made free of charge.
                  Substantive amendments, supplementary reports, or responses to
                  Part 35 questions may incur additional fees, which will be
                  quoted in advance.
                </p>
              </section>

              {/* 7. Data Protection & Safety */}
              <section id="data-protection" className="mb-14" data-id="element-1214">
                <h2 className="text-2xl font-bold text-[#1A2B3C] mb-6" data-id="element-1215">
                  7. Data Protection & Safety
                </h2>

                <h3 className="text-lg font-bold text-[#1A2B3C] mb-3" data-id="element-1216">
                  7.1 Data Roles
                </h3>
                <p className="text-ink-600 leading-relaxed mb-6" style={{
                lineHeight: '1.8'
              }} data-id="element-1217">
                  In accordance with the UK GDPR, the Instructing Party is the
                  Data Controller and the Agency is the Data Processor. We
                  handle all information in strict confidence and in compliance
                  with the Data Protection Act 2018. See our{' '}
                  <a href="/privacy-policy" className="text-teal-600 hover:text-teal-700 font-semibold transition-colors" data-id="element-1218">
                    Privacy & Cookie Policy
                  </a>{' '}
                  for full details.
                </p>

                <h3 className="text-lg font-bold text-[#1A2B3C] mb-3" data-id="element-1219">
                  7.2 Subject Safety
                </h3>
                <p className="text-ink-600 leading-relaxed" style={{
                lineHeight: '1.8'
              }} data-id="element-1220">
                  The Instructing Party must inform us of any known risks
                  regarding the Subject prior to an assessment. This includes,
                  but is not limited to, a history of violence, substance
                  misuse, or any safeguarding concerns. If a risk is identified,
                  we reserve the right to arrange an accompanied assessment at
                  an additional cost, or to decline the instruction where the
                  risk cannot be adequately managed.
                </p>
              </section>

              {/* 8. Intellectual Property */}
              <section id="intellectual-property" className="mb-14" data-id="element-1221">
                <h2 className="text-2xl font-bold text-[#1A2B3C] mb-6" data-id="element-1222">
                  8. Intellectual Property
                </h2>
                <p className="text-ink-600 leading-relaxed mb-6" style={{
                lineHeight: '1.8'
              }} data-id="element-1223">
                  All Reports remain the intellectual property of the Agency and
                  the authoring Expert. A non-exclusive licence is granted to
                  the Instructing Party for use in the specific legal
                  proceedings for which the Report was commissioned.
                </p>
                <p className="text-ink-600 leading-relaxed" style={{
                lineHeight: '1.8'
              }} data-id="element-1224">
                  Reports may not be reproduced, distributed, or used for any
                  other purpose without prior written consent from the Agency.
                  Unauthorised use or distribution of Reports may result in
                  legal action.
                </p>
              </section>

              {/* 9. Liability */}
              <section id="liability" className="mb-14" data-id="element-1225">
                <h2 className="text-2xl font-bold text-[#1A2B3C] mb-6" data-id="element-1226">
                  9. Liability
                </h2>

                <div className="bg-warm-50 border border-[#E2E8F0] rounded-2xl p-6 mb-6" data-id="element-1227">
                  <p className="text-ink-900 font-bold mb-2" data-id="element-1228">Liability Cap</p>
                  <p className="text-ink-600 text-sm leading-relaxed" data-id="element-1229">
                    Our liability is limited to the total fees paid for the
                    specific instruction. We maintain comprehensive professional
                    indemnity insurance for all services provided.
                  </p>
                </div>

                <h3 className="text-lg font-bold text-[#1A2B3C] mb-3" data-id="element-1230">
                  9.1 Expert Opinions
                </h3>
                <p className="text-ink-600 leading-relaxed mb-6" style={{
                lineHeight: '1.8'
              }} data-id="element-1231">
                  Expert opinions are provided in good faith based on available
                  information. We are not liable for case outcomes, judicial
                  decisions, or any consequential losses arising from the
                  content of a Report.
                </p>

                <h3 className="text-lg font-bold text-[#1A2B3C] mb-3" data-id="element-1232">
                  9.2 Expert Non-Attendance
                </h3>
                <p className="text-ink-600 leading-relaxed mb-5" style={{
                lineHeight: '1.8'
              }} data-id="element-1233">
                  In the event of Expert non-attendance at a scheduled
                  examination or court hearing, our liability is limited to:
                </p>
                <ul className="space-y-2 mb-6" data-id="element-1234">
                  {['Full refund of the relevant appointment or court attendance fee', 'Reimbursement of reasonable Subject travel expenses for the missed appointment', 'Expedited replacement appointment at no additional cost', 'Reasonable wasted costs up to a maximum of £2,500 for court non-attendance'].map((item, i) => <li key={i} className="flex items-start gap-3 text-ink-600" data-id="element-1235">
                      <span className="text-teal-500 mt-1 font-bold" data-id="element-1236">•</span>
                      <span style={{
                    lineHeight: '1.8'
                  }} data-id="element-1237">
                        {item}
                      </span>
                    </li>)}
                </ul>
                <p className="text-ink-600 leading-relaxed" style={{
                lineHeight: '1.8'
              }} data-id="element-1238">
                  Our liability does not extend to consequential losses, case
                  outcomes, or third-party claims.
                </p>
              </section>

              {/* 10. Expert Allocation */}
              <section id="expert-allocation" className="mb-14" data-id="element-1239">
                <h2 className="text-2xl font-bold text-[#1A2B3C] mb-6" data-id="element-1240">
                  10. Expert Allocation & Substitution
                </h2>
                <p className="text-ink-600 leading-relaxed mb-6" style={{
                lineHeight: '1.8'
              }} data-id="element-1241">
                  We allocate Experts based on case requirements, specialism,
                  geographical proximity, and availability. While we endeavour
                  to meet specific Expert requests, allocation is at our
                  discretion. We guarantee that all allocated Experts are
                  GMC-registered, hold appropriate specialist qualifications,
                  and maintain current professional indemnity insurance.
                </p>
                <p className="text-ink-600 leading-relaxed" style={{
                lineHeight: '1.8'
              }} data-id="element-1242">
                  We reserve the right to substitute an allocated Expert with an
                  alternative of equivalent or higher qualification if the
                  original Expert becomes unavailable. We will notify the
                  Instructing Party of any substitution and seek approval before
                  proceeding, except in urgent circumstances where delay would
                  prejudice the case.
                </p>
              </section>

              {/* 11. Complaints */}
              <section id="complaints" className="mb-14" data-id="element-1243">
                <h2 className="text-2xl font-bold text-[#1A2B3C] mb-6" data-id="element-1244">
                  11. Complaints Procedure
                </h2>
                <p className="text-ink-600 leading-relaxed mb-6" style={{
                lineHeight: '1.8'
              }} data-id="element-1245">
                  We are committed to providing excellent service. If you are
                  dissatisfied with any aspect of our service, please contact us
                  immediately:
                </p>
                <div className="bg-warm-50 border border-[#E2E8F0] rounded-2xl p-6 mb-6" data-id="element-1246">
                  <div className="flex items-center gap-3" data-id="element-1247">
                    <Mail className="h-5 w-5 text-teal-600 flex-shrink-0" data-id="element-1248" />
                    <a href="mailto:info@uk-medicolegal-assessment.com" className="text-teal-600 hover:text-teal-700 font-bold text-lg transition-colors" data-id="element-1249">
                      info@uk-medicolegal-assessment.com
                    </a>
                  </div>
                </div>
                <p className="text-ink-600 leading-relaxed" style={{
                lineHeight: '1.8'
              }} data-id="element-1250">
                  We will acknowledge complaints within 2 working days and
                  provide a full written response within 28 days. If you remain
                  dissatisfied, you may escalate the complaint to the relevant
                  professional regulatory body.
                </p>
              </section>

              {/* 12. Termination */}
              <section id="termination" className="mb-14" data-id="element-1251">
                <h2 className="text-2xl font-bold text-[#1A2B3C] mb-6" data-id="element-1252">
                  12. Termination
                </h2>
                <p className="text-ink-600 leading-relaxed mb-6" style={{
                lineHeight: '1.8'
              }} data-id="element-1253">
                  Either party may terminate the agreement by giving written
                  notice. Fees for work completed up to the termination date
                  remain payable. See our{' '}
                  <a href="/cancellation-policy" className="text-teal-600 hover:text-teal-700 font-semibold transition-colors" data-id="element-1254">
                    Cancellation Policy
                  </a>{' '}
                  for appointment-specific cancellation terms and charges.
                </p>
                <p className="text-ink-600 leading-relaxed" style={{
                lineHeight: '1.8'
              }} data-id="element-1255">
                  Instructions may not be transferred to another party without
                  our prior written consent. The original Instructing Party
                  remains liable for all fees incurred up to the date of any
                  approved transfer. An administrative fee of £75 applies to all
                  transfer requests.
                </p>
              </section>

              {/* 13. Governing Law */}
              <section id="governing-law" className="mb-14" data-id="element-1256">
                <h2 className="text-2xl font-bold text-[#1A2B3C] mb-6" data-id="element-1257">
                  13. Governing Law
                </h2>
                <p className="text-ink-600 leading-relaxed mb-6" style={{
                lineHeight: '1.8'
              }} data-id="element-1258">
                  These Terms and Conditions are governed by the laws of England
                  and Wales. Any disputes will be subject to the exclusive
                  jurisdiction of the courts of England and Wales.
                </p>
                <p className="text-ink-600 leading-relaxed" style={{
                lineHeight: '1.8'
              }} data-id="element-1259">
                  We reserve the right to update these terms. Changes will be
                  posted on our website with the updated effective date.
                  Continued use of our services following any changes
                  constitutes acceptance of the revised terms.
                </p>
              </section>

              {/* Contact footer */}
              <div className="bg-warm-50 border border-[#E2E8F0] rounded-2xl p-6 md:p-8" data-id="element-1260">
                <h3 className="text-lg font-bold text-[#1A2B3C] mb-3" data-id="element-1261">
                  Questions About These Terms?
                </h3>
                <p className="text-ink-600 leading-relaxed mb-4" style={{
                lineHeight: '1.8'
              }} data-id="element-1262">
                  If you have any questions about these Terms and Conditions, or
                  require a framework or volume agreement, please contact us:
                </p>
                <p className="text-ink-700" data-id="element-1263">
                  <strong data-id="element-1264">Email:</strong>{' '}
                  <a href="mailto:info@uk-medicolegal-assessment.com" className="text-teal-600 hover:text-teal-700 font-semibold transition-colors" data-id="element-1265">
                    info@uk-medicolegal-assessment.com
                  </a>
                </p>
              </div>
            </main>
          </div>
        </div>
      </div>

      <Footer data-id="element-1266" />
    </div>;
}